import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useHistory, Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

// UI Components
import { Form, Button, Icon } from "semantic-ui-react";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import { DateInput } from "semantic-ui-calendar-react";
import "semantic-ui-css/semantic.min.css";

// Custom Components
import HeadDetail from "../../../../components/headDetail";
import HeadDetailReservation from "../../../../components/headDetailReservation";
import NoteSelector from "../../../../components/NoteSelector";
import GenderIcon from "../../../../components/reservation/genderIcon";

// Redux Actions
import { createPassengerBySpace } from "../../../../redux/actions/reservations/spacePassenger/createPassengerBySpace";
import { initialStatePassengerBySpace } from "../../../../redux/actions/reservations/spacePassenger/fetchPassengerBySpace";
import { updatePassengerBySpace } from "../../../../redux/actions/reservations/spacePassenger/updatePassengerBySpace";
import { verifyPassenger, initialStateVerifyPassenger } from "../../../../redux/actions/reservations/spacePassenger/verifyPassenger";
import { fetchPassengerBySpaceFormCustom, initialStatePassengerBySpaceFormCustom } from "../../../../redux/slices/custom/reservations/cruise/passengerBySpace/customPassengerBySpaceForm.slice";

// Utils
import { scrollToTop } from "../../../resources/utils";
import { status } from "../../../resources/optionsList";

const calculateBirthdateInCruiseRange = (birthdate, startDate, endDate) => {
  const adjustedBirthdate = new Date(birthdate);
  adjustedBirthdate.setFullYear(startDate.getFullYear());
  return adjustedBirthdate >= startDate && adjustedBirthdate <= endDate;
};

const filterNotesByType = (notes, type) => {
  return notes?.filter((note) => note?.type === type) || [];
};

const BreadcrumbNavigation = ({ cruiseId, reservationId, bookingId, cruiseCode, cabinName, t }) => (
  <div className="ui breadcrumb">
    <Link to={`/cruises/${cruiseId}/cruise-detail`}>
      <div className="active section">{cruiseCode}</div>
    </Link>
    <div className="divider">/</div>
    <div className="active section">
      <Link to={`/cruises/${cruiseId}/reservations`}>
        {t("reservation_title")}
      </Link>
    </div>

    <div className="divider">/</div>
    <div className="active section">
      <Link
        to={`/cruises/${cruiseId}/reservations/${reservationId}/bookings`}
      >
        {t("cabin_title")}
      </Link>
    </div>
    <div className="divider">/</div>
    <div className="active section">
      {t("cabin_cabin")} {cabinName}
    </div>
    <div className="divider">/</div>
    <div className="active section">
      <Link
        to={`/cruises/${cruiseId}/reservations/${reservationId}/bookings/${bookingId}/spaces`}
      >
        {t("space_title")}
      </Link>
    </div>
    <div className="divider">/</div>
    <div className="active section">{t("passenger_path")}</div>
  </div>
);

export default function PassengerBySpaceForm() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { register, handleSubmit, errors, getValues, setValue, watch } = useForm();

  const {
    cruise_id,
    reservation_id,
    booking_id,
    passenger_id,
    space_id,
    spacePassenger_id,
  } = useParams();

  const passengerBySpaceStateCustom = useSelector((state) => state.customPassengerBySpaceForm);
  const passengerBySpaceState = useSelector((state) => state.passengerBySpaceReducer);
  const verifyPassengerState = useSelector((state) => state.verifyPassengerReducer);

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });

  const [transferState, setTransferState] = useState({});

  const handleNotesTransferChange = (field, value) => {
    setTransferState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const [birthdateInCruise, setBirthdateInCruise] = useState(false);
  const [passengerId, setPassengerId] = useState(null);

  const filteredNotes = useMemo(
    () => ({
      identification: filterNotesByType(
        passengerBySpaceStateCustom.identificationTypes,
        "passenger"
      ),
      food: filterNotesByType(passengerBySpaceStateCustom.notes, "food"),
      medical: filterNotesByType(passengerBySpaceStateCustom.notes, "medical"),
      wetsuits8Days: filterNotesByType(
        passengerBySpaceStateCustom.notes,
        "wetsuits 8 days"
      ),
      wetsuits15Days: filterNotesByType(
        passengerBySpaceStateCustom.notes,
        "wetsuits 15 days"
      ),
      special: filterNotesByType(passengerBySpaceStateCustom.notes, "special"),
      transfer: filterNotesByType(
        passengerBySpaceStateCustom.notes,
        "transfer"
      ),
    }),
    [
      passengerBySpaceStateCustom.identificationTypes,
      passengerBySpaceStateCustom.notes,
    ]
  );

  useEffect(() => {
    dispatch(
      fetchPassengerBySpaceFormCustom(
        "",
        space_id,
        spacePassenger_id,
        cruise_id,
        reservation_id,
        booking_id
      )
    );
    scrollToTop();

    return () => {
      dispatch(initialStatePassengerBySpaceFormCustom());
      dispatch(initialStatePassengerBySpace());
      dispatch(initialStateVerifyPassenger());
    };
  }, [dispatch, space_id, spacePassenger_id, cruise_id, reservation_id, booking_id]);

  useEffect(() => {
    if (!passengerBySpaceStateCustom) return;

    if (spacePassenger_id && Object.keys(passengerBySpaceStateCustom.passengerBySpace).length) {
      populateExistingPassenger(passengerBySpaceStateCustom.passengerBySpace);
      setBirthdateInCruise(passengerBySpaceStateCustom.passengerBySpace.birthdateInCruise);
    } else if (
      passengerBySpaceStateCustom.space &&
      Object.keys(passengerBySpaceStateCustom.space).length
    ) {
      setValue("gender_id", passengerBySpaceStateCustom.space.gender_id);
    }
  }, [spacePassenger_id, passengerBySpaceStateCustom, setValue]);

  useEffect(() => {
    if (verifyPassengerState.message !== "passenger exist") return;

    const { passenger } = verifyPassengerState;
    populateVerifiedPassenger(passenger);

    if (passengerBySpaceStateCustom.cruise) {
      const startDate = new Date(
        passengerBySpaceStateCustom.cruise.start_date_flight
      );
      const endDate = new Date(passengerBySpaceStateCustom.cruise.end_date_flight);
      const isInRange = calculateBirthdateInCruiseRange(
        passenger.birthdate,
        startDate,
        endDate
      );
      setBirthdateInCruise(isInRange);
    }

    setPassengerId(passenger.id);
  }, [verifyPassengerState.message, verifyPassengerState.passenger, passengerBySpaceStateCustom.cruise]);

  useEffect(() => {
    const successMessages = ["resource created successfully", "resource updated successfully"];
    if (successMessages.includes(passengerBySpaceState.message)) {
      history.go(-1);
    }
  }, [passengerBySpaceState.message, history]);


  const populateExistingPassenger = (passenger) => {
    const fieldsMap = {
      identification: "identification",
      identification_type_id: "identification_type_id",
      last_name: "last_name",
      first_name: "first_name",
      email: "email",
      birthdate: "birthdate",
      country_id: "country_id",
      gender_id: "gender_id",
      feed: "feed",
      medical_information: "medical_information",
      rent_wetsuits: "rent_wetsuits",
      special_requirements: "special_requirements",
    };

    Object.entries(fieldsMap).forEach(([fieldName, key]) => {
      if (passenger[key] !== undefined) {
        setValue(fieldName, passenger[key]);
      }
    });
  };

  const populateVerifiedPassenger = (passenger) => {
    const verificationFields = {
      identification_type_id: "identification_type_id",
      last_name: "last_name",
      first_name: "first_name",
      email: "email",
      birthdate: "birthdate",
      country_id: "country_id",
      gender_id: "gender_id",
    };

    Object.entries(verificationFields).forEach(([fieldName, key]) => {
      setValue(fieldName, passenger[key]);
    });
  };

  // ============ EVENT HANDLERS ============

  const handleSearchPassenger = useCallback(() => {
    const identification = getValues("identification")?.trim();
    if (identification) {
      dispatch(verifyPassenger(identification));
    }
  }, [dispatch, getValues]);

  const handleBirthdate = useCallback(
    (value) => {
      if (!passengerBySpaceStateCustom.cruise || !value) return;

      const birthdate = new Date(value);
      const startDate = new Date(
        passengerBySpaceStateCustom.cruise.start_date_flight
      );
      const endDate = new Date(
        passengerBySpaceStateCustom.cruise.end_date_flight
      );

      const isInRange = calculateBirthdateInCruiseRange(
        birthdate,
        startDate,
        endDate
      );
      setBirthdateInCruise(isInRange);
    },
    [passengerBySpaceStateCustom.cruise]
  );


  const handleInsertNoteTransfer = useCallback(() => {
    const currentNotes = getValues("transfer_notes") || "";
    const noteTransfer = transferState.noteTransfer || "";
    const selectedDate = transferState.date || "";

    setValue("transfer_notes", `${currentNotes}\n${selectedDate ? selectedDate : ""} ${noteTransfer}`.trim());

    setTransferState({
      noteTransfer: "",
      cleanList: "",
      date: "",
    });
  }, [transferState]);

  const handleShowMessage = useCallback((newState = {}) => {
    setSnackbarState((prev) => ({ ...prev, open: true, ...newState }));
    const timer = setTimeout(() => {
      setSnackbarState((prev) => ({ ...prev, open: false }));
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  const onSubmit = useCallback((data) => {
    if (spacePassenger_id) {
      data.spacePassenger_id = spacePassenger_id;
      data.passengerIdByBody = passengerId || passengerBySpaceStateCustom.passengerBySpace?.passenger_id;
      dispatch(updatePassengerBySpace(booking_id, space_id, passenger_id, data));
    } else {
      dispatch(createPassengerBySpace(booking_id, space_id, data));
    }

    handleShowMessage({
      vertical: "top",
      horizontal: "right",
    });
    scrollToTop();
  },
    [
      spacePassenger_id,
      passengerId,
      passengerBySpaceStateCustom.passengerBySpace?.passenger_id,
      dispatch,
      booking_id,
      space_id,
      passenger_id,
      handleShowMessage,
    ]
  );

  if (
    passengerBySpaceStateCustom.fetching ||
    passengerBySpaceState.fetching ||
    !filteredNotes.identification?.length
  ) {
    return <CircularProgress />;
  }

  const { vertical, horizontal, open } = snackbarState;
  const { noteTransfer, cleanList, date } = transferState;
  const initialDate = passengerBySpaceStateCustom?.cruise?.start_date_format;

  return (
    <>
      {passengerBySpaceState.error && (
        <Snackbar
          open={open}
          autoHideDuration={6000}
          anchorOrigin={{ vertical, horizontal }}
        >
          <Alert severity="error" variant="filled">
            {t(passengerBySpaceState.error)}
          </Alert>
        </Snackbar>
      )}

      <BreadcrumbNavigation
        cruiseId={cruise_id}
        reservationId={reservation_id}
        bookingId={booking_id}
        cruiseCode={passengerBySpaceStateCustom.cruise?.code}
        cabinName={passengerBySpaceStateCustom.booking?.cabin_name}
        t={t}
      />
      <br />
      <br />
      <HeadDetail
        yacht_name={passengerBySpaceStateCustom.cruise?.yacht_name}
        itinerary_name={passengerBySpaceStateCustom.cruise?.itinerary_name}
        cruise_name={passengerBySpaceStateCustom.cruise?.name}
        start_date={passengerBySpaceStateCustom.cruise?.start_date_format}
        end_date={passengerBySpaceStateCustom.cruise?.end_date_format}
      />
      <HeadDetailReservation
        reservation_date={passengerBySpaceStateCustom.reservation?.creation_date}
        reservation_agency={passengerBySpaceStateCustom.reservation?.agency_name}
        reservation_executive={passengerBySpaceStateCustom.reservation?.executive}
        reservation_type={passengerBySpaceStateCustom.reservation?.cruise_type}
      />
      <br />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <label className="flex items-center justify-between my-2">
          {t("passenger_information")}
          <GenderIcon gender={passengerBySpaceStateCustom.space?.gender_name} />
        </label>
        <Divider style={{ marginBottom: '15px' }} variant="fullWidth" />

        <Form.Group widths="4">
          <Form.Field required>
            <label>{t("passenger_identification")}</label>
            <input
              placeholder={t("passenger_identification")}
              name="identification"
              ref={register({ required: true, minLength: 3 })}
              style={{ textTransform: "uppercase" }}
            />
            {errors.identification && (
              <span className="error_message">{t("required_information")}</span>
            )}
          </Form.Field>
          <Form.Field>
            <Button
              icon
              className="!mt-[24px]"
              color="blue"
              type="button"
              onClick={handleSearchPassenger}
              loading={verifyPassengerState.fetching}
            >

              {t("search")}
            </Button>
          </Form.Field>
          <Form.Field required>
            <label>{t("passenger_identificationType")}</label>
            <select
              name="identification_type_id"
              ref={register({ required: true })}
            >
              <option value=""></option>
              {filteredNotes.identification?.map((idType) => (
                <option key={idType.id} value={idType.id}>
                  {t(idType.name)}
                </option>
              ))}
            </select>
            {errors.identification_type_id && (
              <span className="error_message">{t("required_information")}</span>
            )}
          </Form.Field>
        </Form.Group>
        <br />
        <label>{t("general_information_tag")}</label>
        <Divider style={{ marginBottom: '15px' }} variant="fullWidth" />
        <Form.Group widths="equal">
          <Form.Field required>
            <label>{t("passenger_lastName")}</label>
            <input
              placeholder={t("passenger_lastName")}
              name="last_name"
              style={{ textTransform: "uppercase" }}
              ref={register({ required: true, minLength: 2 })}
            />
            {errors.last_name && (
              <span className="error_message">{t("required_information")}</span>
            )}
          </Form.Field>
          <Form.Field required>
            <label>{t("passenger_name")}</label>
            <input
              placeholder={t("passenger_name")}
              name="first_name"
              style={{ textTransform: "uppercase" }}
              ref={register({ required: true, minLength: 2 })}
            />
            {errors.first_name && (
              <span className="error_message">{t("required_information")}</span>
            )}
          </Form.Field>
          <Form.Field>
            <label>{t("passenger_email")}</label>
            <input
              placeholder={t("passenger_email")}
              name="email"
              type="email"
              ref={register({
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              })}
            />
            {errors.email && (
              <span className="error_message">{t("invalid_email")}</span>
            )}
          </Form.Field>
          <Form.Field required>
            <label>
              {t("passenger_birthday")}{" "}
              <span className="text_date_format">(dd-mm-yyyy)</span>
            </label>
            <input
              placeholder={t("passenger_birthday")}
              type="date"
              name="birthdate"
              onChange={(event) => handleBirthdate(event.target.value)}
              ref={register({ required: true })}
            />
            {errors.birthdate && (
              <span className="error_message">{t("required_information")}</span>
            )}
          </Form.Field>
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field required>
            <label>{t("passenger_country")}</label>
            <select name="country_id" ref={register({ required: true })}>
              <option value=""></option>
              {passengerBySpaceStateCustom.countries?.map((country) => (
                <option key={country.id} value={country.id}>
                  {t(country.name)}
                </option>
              ))}
            </select>
            {errors.country_id && (
              <span className="error_message">{t("required_information")}</span>
            )}
          </Form.Field>
          <Form.Field required>
            <label>{t("passenger_gender")}</label>
            <select name="gender_id" ref={register({ required: true })}>
              <option value=""></option>
              {passengerBySpaceStateCustom.genders?.map((gender) => (
                <option key={gender.id} value={gender.id}>
                  {t(gender.name)}
                </option>
              ))}
            </select>
            {errors.gender_id && (
              <span className="error_message">{t("required_information")}</span>
            )}
          </Form.Field>
          <Form.Field>
            <label>{t("passenger_marital_satatus")}</label>
            <select
              name="marital_status"
              defaultValue={
                passengerBySpaceStateCustom.passengerBySpace?.marital_status || ""
              }
              ref={register()}
            >
              <option value=""></option>
              {status.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.name)}
                </option>
              ))}
            </select>
          </Form.Field>
          <Form.Field>
            <Form.Group width="equal">
              <Form.Field>
                <label>{t("passenger_vegetarian")}</label>
                <div className="ui checkbox">
                  <input
                    type="checkbox"
                    name="vegetarian"
                    ref={register}
                    defaultChecked={
                      passengerBySpaceStateCustom.passengerBySpace?.vegetarian
                    }
                  />
                  <label></label>
                </div>
              </Form.Field>
              <Form.Field>
                <label>{t("passenger_glutenFree")}</label>
                <div className="ui checkbox">
                  <input
                    type="checkbox"
                    name="gluten_free"
                    ref={register}
                    defaultChecked={
                      passengerBySpaceStateCustom.passengerBySpace?.gluten_free
                    }
                  />
                  <label></label>
                </div>
              </Form.Field>
              <Form.Field>
                <label>{t("passenger_lactose_intolerant")}</label>
                <div className="ui checkbox">
                  <input
                    type="checkbox"
                    name="lactose_intolerant"
                    ref={register}
                    defaultChecked={
                      passengerBySpaceStateCustom.passengerBySpace
                        ?.lactose_intolerant
                    }
                  />
                  <label></label>
                </div>
              </Form.Field>
            </Form.Group>
          </Form.Field>
        </Form.Group>
        <br />
        <label>{t("passenger_payments")}</label>
        <Divider style={{ marginBottom: '15px' }} variant="fullWidth" />
        <Form.Group width="equal">
          <Form.Field width="8">
            <label>{t("passenger_tct")}</label>
            <div className="ui checkbox">
              <input
                type="checkbox"
                name="tct"
                defaultChecked={passengerBySpaceStateCustom.passengerBySpace?.tct}
                ref={register}
              />
              <label></label>
            </div>
          </Form.Field>
          <Form.Field>
            <label>{t("passenger_gps")}</label>
            <div className="ui checkbox">
              <input
                type="checkbox"
                name="gps"
                defaultChecked={passengerBySpaceStateCustom.passengerBySpace?.gps}
                ref={register}
              />
              <label></label>
            </div>
          </Form.Field>
        </Form.Group>
        <br />
        <label>{t("passenger_notes")}</label>
        <Divider style={{ marginBottom: '15px' }} variant="fullWidth" />
        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("passenger_aditional_notes_b2c")}</label>
            <textarea
              rows={5}
              disabled
              placeholder={t("passenger_aditional_notes_b2c")}
              name="aditional_notes"
              defaultValue={
                passengerBySpaceStateCustom.passengerBySpace?.aditional_notes || ""
              }
            />
          </Form.Field>
        </Form.Group>
        <NoteSelector
          label={t("passenger_foodNotes")}
          name="feed"
          notes={filteredNotes.food}
          register={register}
          setValue={setValue}
          watch={watch}
          t={t}
        />
        <NoteSelector
          label={t("passenger_medicalNotes")}
          name="medical_information"
          notes={filteredNotes.medical}
          register={register}
          setValue={setValue}
          watch={watch}
          t={t}
        />
        <NoteSelector
          label={t("passenger_wetsuitsNotes_8days")}
          labelTwo={t("passenger_wetsuitsNotes_15days")}
          name="rent_wetsuits"
          notes={filteredNotes.wetsuits8Days}
          notesTwo={filteredNotes.wetsuits15Days}
          register={register}
          setValue={setValue}
          watch={watch}
          t={t}
        />
        <NoteSelector
          label={t("passenger_specialNotes")}
          name="special_requirements"
          notes={filteredNotes.special}
          register={register}
          setValue={setValue}
          watch={watch}
          t={t}
          birthdateInCruise={birthdateInCruise}
        />
        <br />
        <label>{t("passenger_transfers")}</label>
        <Divider style={{ marginBottom: '15px' }} variant="fullWidth" />
        <Form.Group>
          <Form.Field width="4">
            <label>{t("passenger_day")}</label>
            <DateInput
              name="day"
              placeholder={t("dd/mm/yyyy")}
              value={date}
              onChange={(e, { value }) => handleNotesTransferChange('date', value)}
              iconPosition="right"
              popupPosition="bottom right"
              closable
              initialDate={initialDate}
              dateFormat="DD/MM/YYYY"
            />
          </Form.Field>
          <Form.Field>
            <label>{t("passenger_message")}</label>
            <select
              value={noteTransfer}
              defaultValue={cleanList}
              onChange={(e) => handleNotesTransferChange('noteTransfer', e.target.value)}
            >
              <option value=""></option>
              {filteredNotes.transfer?.map((note) => (
                <option key={note.id} value={note.message}>
                  {note.message}
                </option>
              ))}
            </select>
          </Form.Field>
          <Form.Field>
            <br />
            <Button
              icon
              color="blue"
              type="button"
              onClick={handleInsertNoteTransfer}
            >
              <Icon name="plus" />
            </Button>
          </Form.Field>
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field>
            <textarea
              rows={3}
              placeholder={t("passenger_notesTransfer")}
              name="transfer_notes"
              ref={register}
              defaultValue={passengerBySpaceStateCustom.passengerBySpace?.transfer_notes || ""}
            />
          </Form.Field>
        </Form.Group>
        <Divider style={{ marginBottom: '15px' }} variant="middle" />
        <br />
        <button
          type="button"
          className="ui button"
          onClick={() => history.go(-1)}
        >
          {t("canceled_button")}
        </button>
        <button
          type="submit"
          className={
            passengerBySpaceState.processing
              ? "ui loading primary button"
              : "ui primary button"
          }
          disabled={passengerBySpaceState.processing}
        >
          {t("save_button")}
        </button>
      </Form>
    </>
  );
}
